// DeferredActionLogList.jsx
import React, {useMemo, useState} from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    IconButton,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * @param {Object[]} logs - список логов
 * @param {number}   logs[].id
 * @param {"SUCCESS"|"CHECK_CONDITION_FAILED"|"FAILED"|"AI_EXECUTE"} logs[].status
 * @param {string}   logs[].error
 * @param {string}   logs[].createdAt - ISO datetime
 * @param {Object}   logs[].user
 * @param {Object}   statusCounts - counts by status from backend (optional)
 * @param {Object}   errorTypeCounts - counts by error type from backend (optional)
 * @param {number}   total - total logs count from backend (optional)
 * @param {boolean}  hasNext - slice.hasNext from backend (optional)
 * @param {Function} onLoadMore - () => void (optional)
 * @param {boolean}  loading - indicates logs page loading (optional)
 */
export default function DeferredActionLogList({
                                                  logs = [],
                                                  statusCounts = null,
                                                  errorTypeCounts = null,
                                                  total = null,
                                                  hasNext = false,
                                                  onLoadMore,
                                                  loading = false,
                                              }) {
    const STATUSES = ["SUCCESS", "CHECK_CONDITION_FAILED", "FAILED", "AI_EXECUTE"];
    const ERROR_TYPES = ["TELEGRAM_BLOCKED_BY_USER", "TELEGRAM_CHAT_NOT_FOUND", "USER_DISABLED_MAILING", "UNKNOWN"];
    const [openById, setOpenById] = useState({});

    const STATUS_META = {
        SUCCESS: {
            label: "Успешно",
            chipColor: "success",
            icon: <CheckCircleIcon />,
        },
        CHECK_CONDITION_FAILED: {
            label: "Условие не выполнено",
            chipColor: "warning",
            icon: <CancelIcon />,
        },
        AI_EXECUTE: {
            label: "Пропущено (AI)",
            chipColor: "info",
            icon: <SmartToyIcon />,
        },
        FAILED: {
            label: "Ошибка",
            chipColor: "error",
            icon: <ErrorOutlineIcon />,
        },
    };

    const fmtDate = (iso) => {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso || "—";
        }
    };

    const statusChip = (status) => {
        const meta = STATUS_META[status] || {
            label: String(status ?? "—"),
            chipColor: "default",
            icon: null,
        };

        return (
            <Chip
                size="small"
                color={meta.chipColor}
                icon={meta.icon}
                label={meta.label}
            />
        );
    };

    // ✅ group by status (fallback when backend doesn't provide counts)
    const statusCountsFallback = useMemo(() => {
        const acc = Object.fromEntries(STATUSES.map((s) => [s, 0]));
        let unknown = 0;

        for (const log of logs) {
            const s = log?.status;
            if (acc[s] !== undefined) acc[s] += 1;
            else unknown += 1;
        }

        return { ...acc, UNKNOWN: unknown };
    }, [logs]);

    const counts = useMemo(() => {
        if (!statusCounts) return statusCountsFallback;
        const acc = Object.fromEntries(STATUSES.map((s) => [s, 0]));
        let unknown = 0;
        for (const [k, v] of Object.entries(statusCounts || {})) {
            if (acc[k] !== undefined) acc[k] = Number(v) || 0;
            else unknown += Number(v) || 0;
        }
        return { ...acc, UNKNOWN: unknown };
    }, [statusCounts, statusCountsFallback]);

    const totalCount = total != null ? total : (statusCounts ? Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0) : logs.length);
    const errorTypeLabels = {
        TELEGRAM_BLOCKED_BY_USER: "Telegram: пользователь заблокировал",
        TELEGRAM_CHAT_NOT_FOUND: "Telegram: чат не найден",
        USER_DISABLED_MAILING: "Пользователь отключил рассылку",
        UNKNOWN: "Неизвестно",
    };
    const errorTypeChipColors = {
        TELEGRAM_BLOCKED_BY_USER: "warning",
        TELEGRAM_CHAT_NOT_FOUND: "error",
        USER_DISABLED_MAILING: "primary",
        UNKNOWN: "default",
    };
    const errorCounts = useMemo(() => {
        const acc = Object.fromEntries(ERROR_TYPES.map((s) => [s, 0]));
        if (!errorTypeCounts) return acc;

        for (const [k, v] of Object.entries(errorTypeCounts || {})) {
            if (acc[k] !== undefined) {
                acc[k] = Number(v) || 0;
            } else {
                acc.UNKNOWN += Number(v) || 0;
            }
        }
        return acc;
    }, [errorTypeCounts]);
    const hasErrorTypeStats = !!errorTypeCounts;
    const nonZeroErrorTypes = ERROR_TYPES.filter((type) => (errorCounts[type] || 0) > 0);

    const toggleRow = (id) => {
        setOpenById((prev) => ({...prev, [id]: !prev?.[id]}));
    };

    const renderUser = (u) => {
        if (!u) {
            return (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    —
                </Typography>
            );
        }

        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>U</Avatar>
                <Box>
                    <Typography variant="body2">{`ID: ${u.id}`}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Telegram: {u.telegramId}
                    </Typography>
                </Box>
            </Stack>
        );
    };

    const parseAiReason = (raw) => {
        if (!raw) return null;
        const t = String(raw);
        if (t.startsWith("AI_DECISION_SKIP:")) {
            return {title: "Пропущено (AI): AI решил не выполнять действие", details: t.replace(/^AI_DECISION_SKIP:\s*/, "")};
        }
        if (t.startsWith("AI_DECISION_NOT_CHOSEN")) {
            // Example: AI_DECISION_NOT_CHOSEN (chosen=1): <raw>
            const m = t.match(/^AI_DECISION_NOT_CHOSEN\s*\(chosen=(-?\d+)\)\s*:\s*([\s\S]*)$/);
            if (m) {
                return {
                    title: `Пропущено (AI): выбрано другое действие (chosen=${m[1]})`,
                    details: m[2],
                };
            }
            return {title: "Пропущено (AI): выбрано другое действие", details: t};
        }
        return {title: "Пропущено (AI)", details: t};
    };

    const renderResult = (log) => {
        const status = log?.status;
        const raw = log?.error;

        if (status === "SUCCESS") {
            return {
                title: "Действие выполнено",
                detailsLabel: "Условие (с подстановкой параметров)",
                details: raw,
            };
        }

        if (status === "CHECK_CONDITION_FAILED") {
            return {
                title: "Действие не выполнено: условие не прошло",
                detailsLabel: "Условие (с подстановкой параметров)",
                details: raw,
            };
        }

        if (status === "AI_EXECUTE") {
            const ai = parseAiReason(raw);
            return {
                title: ai?.title || "Пропущено (AI)",
                detailsLabel: "Ответ AI",
                details: ai?.details || raw,
            };
        }

        // FAILED or unknown
        if (!raw) {
            return (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    —
                </Typography>
            );
        }

        const text = String(raw);
        // Friendly one-liners for common failures
        if (text.includes("VK sendMessage failed")) {
            return {title: "Ошибка: не удалось отправить сообщение VK", detailsLabel: "Технические детали", details: text};
        }
        if (text.includes("Telegram id is null")) {
            return {title: "Ошибка: отсутствует Telegram ID", detailsLabel: "Технические детали", details: text};
        }
        if (text.includes("VK peer_id is null")) {
            return {title: "Ошибка: отсутствует VK ID", detailsLabel: "Технические детали", details: text};
        }
        return {title: "Ошибка при выполнении", detailsLabel: "Технические детали", details: text};
    };

    const renderResultCell = (log) => {
        const res = renderResult(log);
        if (!res || React.isValidElement(res)) {
            return res;
        }

        const open = !!openById?.[log.id];
        const details = res.details != null && String(res.details).trim().length > 0 ? String(res.details) : "";
        const showDetails = details.length > 0;
        const truncated = details.length > 220 ? details.slice(0, 220) + "…" : details;

        return (
            <Box>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Typography variant="body2">{res.title}</Typography>
                    {showDetails && (
                        <IconButton
                            size="small"
                            onClick={() => toggleRow(log.id)}
                            sx={{mt: "-2px"}}
                            aria-label={open ? "Скрыть детали" : "Показать детали"}
                        >
                            {open ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                        </IconButton>
                    )}
                </Stack>

                {showDetails && (
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Typography variant="caption" sx={{opacity: 0.7}}>
                            {res.detailsLabel}
                        </Typography>
                        <Tooltip title={<span style={{whiteSpace: "pre-wrap"}}>{details}</span>} arrow>
                            <Typography variant="body2" component="div">
                                <code style={{whiteSpace: "pre-wrap"}}>{truncated}</code>
                            </Typography>
                        </Tooltip>
                    </Collapse>
                )}
            </Box>
        );
    };

    return (
        <Card>
            <CardContent>
                <Stack spacing={1.2} sx={{ mb: 1.25 }}>
                    <Typography variant="subtitle1">Логи выполнения</Typography>

                    <Stack direction={{xs: "column", sm: "row"}} spacing={1} alignItems={{xs: "flex-start", sm: "center"}}>
                        <Typography variant="body2" sx={{ opacity: 0.78, fontWeight: 500 }}>
                            Всего записей: {totalCount}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                            size="small"
                            color="success"
                            label={`Успешно: ${counts.SUCCESS}`}
                        />
                        <Chip
                            size="small"
                            color="warning"
                            label={`Условие не выполнено: ${counts.CHECK_CONDITION_FAILED}`}
                        />
                        <Chip
                            size="small"
                            color="info"
                            label={`Пропущено (AI): ${counts.AI_EXECUTE}`}
                        />
                        <Chip
                            size="small"
                            color="error"
                            label={`Ошибка: ${counts.FAILED}`}
                        />

                        {counts.UNKNOWN > 0 && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Неизвестно: ${counts.UNKNOWN}`}
                            />
                        )}
                    </Stack>

                    {hasErrorTypeStats && (
                        <Box
                            sx={{
                                p: 1,
                                borderRadius: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "action.hover",
                            }}
                        >
                            <Typography variant="caption" sx={{opacity: 0.8, display: "block", mb: 0.8}}>
                                Типы FAILED-ошибок
                            </Typography>

                            {nonZeroErrorTypes.length === 0 ? (
                                <Typography variant="body2" sx={{opacity: 0.7}}>
                                    Ошибок по типам нет.
                                </Typography>
                            ) : (
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    {nonZeroErrorTypes.map((type) => (
                                        <Chip
                                            key={type}
                                            size="small"
                                            color={errorTypeChipColors[type]}
                                            variant="outlined"
                                            label={`${errorTypeLabels[type]}: ${errorCounts[type]}`}
                                            sx={{fontWeight: 500}}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}
                </Stack>

                {logs.length === 0 ? (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Логи отсутствуют.
                    </Typography>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={80}>ID</TableCell>
                                    <TableCell width={160}>Статус</TableCell>
                                    <TableCell>Детали</TableCell>
                                    <TableCell width={260}>Пользователь</TableCell>
                                    <TableCell width={200}>Создано</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>{log.id}</TableCell>
                                        <TableCell>{statusChip(log.status)}</TableCell>
                                        <TableCell>{renderResultCell(log)}</TableCell>
                                        <TableCell>{renderUser(log.user)}</TableCell>
                                        <TableCell>{fmtDate(log.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {hasNext && (
                            <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                                <Button
                                    variant="outlined"
                                    onClick={onLoadMore}
                                    disabled={loading || !onLoadMore}
                                    startIcon={loading ? <CircularProgress size={18} /> : null}
                                    sx={{ textTransform: "none" }}
                                >
                                    {loading ? "Загрузка..." : "Показать еще"}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
